import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { Input } from '@localagent/ui/components/input';
import { CopyIcon, DataList, TrashIcon } from '@localagent/ui/components/data-list';
import { ListAddButton, ListFilterMenu } from '@localagent/ui/components/list-filter-menu';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { useFilteredList, type FilterValue } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import { cn } from '@localagent/ui/lib/utils';
import { toast } from '@localagent/ui/components/sonner';
import {
  useAgentDefinitions,
  useSystemAgents,
  useAgentGroups,
  useCreateAgentGroup,
  useDeleteAgentGroup,
  useDeleteAgentDefinition,
  useDuplicateAgentDefinition,
  useCopySystemAgent,
} from '../hooks/useAgentDefinitions';
import type {
  PersonaWithConfig,
  SystemPersonaWithConfig,
  PersonaAgentConfig as AgentConfig,
} from '@localagent/sdk';

// A workspace persona and a normalized system persona share one card shape,
// discriminated by `source` — the chip filter keys on this exactly like the
// Processes library keys on `is_system`.
type UnifiedPersona = PersonaWithConfig & { source: 'workspace' | 'system' };

// Source scope: '' = all, 'mine' = workspace customs, 'templates' = seeded
// system personas. It defaults to 'mine' so the seeded template corpus never
// buries the user's own agents on page 1.
type SourceScope = '' | 'mine' | 'templates';

function personaInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}

// Normalize a system persona (a { persona, config } pair with a `category`
// instead of a group) into the workspace card shape, tagged source:'system'.
// Copied from AgentsView so the merged list owns its own assembly.
function systemPersonaToCard(sp: SystemPersonaWithConfig): UnifiedPersona {
  let agent_config: AgentConfig | undefined;
  if (sp.config?.agent_config_json) {
    try {
      agent_config = JSON.parse(sp.config.agent_config_json) as AgentConfig;
    } catch { /* ignore parse errors — card still renders without config */ }
  }
  return {
    id: sp.persona.id,
    name: sp.persona.name,
    description: sp.persona.description,
    emoji: sp.persona.emoji,
    group_id: null,
    group_name: sp.persona.category,
    organization_id: '',
    account_id: '',
    is_default: false,
    created_at: sp.persona.created_at ?? '',
    updated_at: sp.persona.updated_at ?? '',
    latest_config_id: sp.config?.id ?? null,
    agent_config,
    source: 'system',
  };
}

const searchFn = (item: UnifiedPersona, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    item.name.toLowerCase().includes(q) ||
    (item.description?.toLowerCase().includes(q) ?? false)
  );
};

const filterFns: Record<string, (item: UnifiedPersona, value: FilterValue) => boolean> = {
  // Group filter keys on group_id — system templates carry no group_id, so
  // selecting a group naturally scopes to workspace agents in that group.
  group: (item, value) => {
    if (!value || Array.isArray(value)) return true;
    return item.group_id === value;
  },
};

const SOURCE_CHIPS: Array<{ value: SourceScope; label: string; testid: string }> = [
  { value: '', label: 'All', testid: 'agent-filter-all' },
  { value: 'mine', label: 'My Personas', testid: 'agent-filter-mine' },
  { value: 'templates', label: 'Templates', testid: 'agent-filter-templates' },
];

function GroupAddRow({ onCreate, isBusy }: { onCreate: (name: string) => void; isBusy: boolean }) {
  const [newName, setNewName] = useState('');
  const submitNew = () => {
    const name = newName.trim();
    if (!name) return;
    onCreate(name);
    setNewName('');
  };
  return (
    <div className="flex items-center gap-1 p-1">
      <Input
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } }}
        placeholder="New group…"
        data-testid="agent-group-add-input"
        className="h-8 flex-1 text-caption"
      />
      <Button
        size="sm"
        data-testid="agent-group-add-submit"
        className="h-8"
        disabled={!newName.trim() || isBusy}
        onClick={submitNew}
      >
        Add
      </Button>
    </div>
  );
}

export function AgentLibrary() {
  const navigate = useNavigate();

  const { data: workspaceRaw, isLoading: workspaceLoading, error: workspaceError } = useAgentDefinitions();
  const { data: systemRaw, isLoading: systemLoading } = useSystemAgents();
  const { data: groups } = useAgentGroups();

  const createGroup = useCreateAgentGroup();
  const deleteGroup = useDeleteAgentGroup();
  const deleteAgent = useDeleteAgentDefinition();
  const duplicateAgent = useDuplicateAgentDefinition();
  const copySystem = useCopySystemAgent();

  const allAgents = useMemo<UnifiedPersona[]>(() => {
    const workspace: UnifiedPersona[] = (workspaceRaw ?? []).map(p => {
      const group = p.group_id ? groups?.find(g => g.id === p.group_id) : undefined;
      return { ...p, group_name: group?.name ?? p.group_name ?? null, source: 'workspace' };
    });
    const system: UnifiedPersona[] = (systemRaw ?? []).map(systemPersonaToCard);
    return [...workspace, ...system];
  }, [workspaceRaw, systemRaw, groups]);

  // Newest-first so a just-created/duplicated/copied agent lands on page 1
  // (system rows seeded long ago sink lower — chips are how users focus, not
  // sort order). Mirrors AgentProcessLibrary's rationale.
  const sortedAgents = useMemo(
    () =>
      [...allAgents].sort((a, b) => {
        const at = a.created_at ? Date.parse(a.created_at) : 0;
        const bt = b.created_at ? Date.parse(b.created_at) : 0;
        return bt - at;
      }),
    [allAgents],
  );

  const [sourceScope, setSourceScope] = useState<SourceScope>('mine');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleteCascade, setDeleteCascade] = useState(false);

  const scopedAgents = useMemo(
    () =>
      sortedAgents.filter((item) => {
        if (sourceScope === 'mine') return item.source === 'workspace';
        if (sourceScope === 'templates') return item.source === 'system';
        return true;
      }),
    [sortedAgents, sourceScope],
  );

  const { filteredItems, searchValue, onSearchChange, filterValues, onFilterChange } =
    useFilteredList({ items: scopedAgents, searchFn, filterFns });

  const groupScope = (filterValues.group as string | undefined) ?? '';

  const pagination = usePagination({
    totalItems: filteredItems.length,
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
  });

  // Return to page 1 whenever any facet changes.
  useEffect(() => {
    pagination.reset();
  }, [searchValue, sourceScope, groupScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedItems = filteredItems.slice(pagination.startIndex, pagination.endIndex);

  const groupOptions = useMemo(
    () => (groups ?? []).map(g => ({ id: g.id, name: g.name })),
    [groups],
  );

  const handleCreateGroup = useCallback((name: string) => {
    createGroup.mutate(
      { name },
      {
        onSuccess: (group) => {
          onFilterChange('group', group.id);
          toast.success(`Group "${group.name}" created`);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  }, [createGroup, onFilterChange]);

  const handleDeleteGroup = useCallback((groupId: string) => {
    // Deleting a group detaches (ungroups) its member agents backend-side —
    // the same simple, safe default the old "ungroup" delete option used
    // (it deleted the group directly, no per-agent update). No move/reassign
    // modal; group rename is dropped in this round.
    deleteGroup.mutate(groupId, {
      onSuccess: () => {
        if (groupScope === groupId) onFilterChange('group', '');
        toast.success('Group deleted');
      },
      onError: (err) => toast.error(err.message),
    });
  }, [deleteGroup, groupScope, onFilterChange]);

  const renderItem = (item: UnifiedPersona) => (
    <div
      data-testid={`agent-item-${item.id}`}
      className="rounded-lg px-3 py-2 transition-colors hover:bg-accent/50"
    >
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-label font-semibold text-primary">
        {personaInitial(item.name)}
      </div>
      <button
        type="button"
        className={cn('min-w-0 flex-1 text-left', item.source === 'system' && 'cursor-default')}
        onClick={() => { if (item.source === 'workspace') void navigate(`/personas/${item.id}`); }}
      >
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{item.name}</span>
          {item.source === 'system' && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-micro font-medium text-muted-foreground">
              template
            </span>
          )}
          {item.group_name && <Badge variant="secondary">{item.group_name}</Badge>}
        </div>
        {item.description && (
          <p className="mt-0.5 truncate text-body text-muted-foreground">{item.description}</p>
        )}
      </button>
    </div>
    {pendingDeleteId === item.id && (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded bg-destructive/10 px-3 py-2">
        <div className="flex flex-col gap-1">
          <span className="text-body text-destructive">
            {`Delete "${item.name}"? No new Agents or chats can start from it; its Agents keep working unless you delete them too.`}
          </span>
          <label className="flex items-center gap-2 text-body text-foreground">
            <input
              type="checkbox"
              data-testid={`agent-delete-cascade-${item.id}`}
              checked={deleteCascade}
              onChange={(e) => setDeleteCascade(e.target.checked)}
              className="cursor-pointer"
            />
            Also delete its Agents
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPendingDeleteId(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            data-testid={`agent-confirm-delete-${item.id}`}
            disabled={deleteAgent.isPending}
            onClick={() => {
              deleteAgent.mutate({ personaId: item.id, deleteAgents: deleteCascade }, {
                onSuccess: () => setPendingDeleteId(null),
                onError: (err) => toast.error(err.message),
              });
            }}
          >
            {deleteAgent.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    )}
    </div>
  );

  return (
    <div data-testid="agent-library-view">
      <DataList<UnifiedPersona>
        headerLeading={
          <ListFilterMenu
            testId="agent-filters"
            filters={[
              {
                key: 'source',
                label: 'Source',
                testId: 'agent-source-filter',
                value: sourceScope,
                options: SOURCE_CHIPS.map((chip) => ({ value: chip.value, label: chip.label, testId: chip.testid })),
                onChange: (value) => setSourceScope(value as SourceScope),
              },
              {
                key: 'group',
                label: 'Group',
                testId: 'agent-group-filter',
                value: groupScope,
                options: [
                  { value: '', label: 'All groups', testId: 'agent-group-filter-option-all' },
                  ...groupOptions.map((g) => ({
                    value: g.id,
                    label: g.name,
                    testId: `agent-group-filter-option-${g.id}`,
                    trailing: (
                      <Tooltip label={`Delete group "${g.name}"`}>
                        <button
                          type="button"
                          data-testid={`agent-group-delete-${g.id}`}
                          className="rounded p-1 text-muted-foreground opacity-0 hover:bg-accent hover:text-destructive group-hover:opacity-100"
                          disabled={createGroup.isPending || deleteGroup.isPending}
                          onClick={() => handleDeleteGroup(g.id)}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </Tooltip>
                    ),
                  })),
                ],
                onChange: (value) => onFilterChange('group', value),
                footer: (
                  <GroupAddRow
                    onCreate={handleCreateGroup}
                    isBusy={createGroup.isPending || deleteGroup.isPending}
                  />
                ),
              },
            ]}
          />
        }
        itemActions={(item) =>
          item.source === 'system'
            ? [
                {
                  label: 'Copy to My Personas',
                  icon: <CopyIcon />,
                  testId: `agent-copy-${item.id}`,
                  onClick: () => {
                    copySystem.mutate(
                      { systemPersonaId: item.id },
                      {
                        onSuccess: (result) => { void navigate(`/personas/${result.persona_id}`); },
                        onError: (err) => toast.error(err.message),
                      },
                    );
                  },
                },
              ]
            : [
                ...(item.agent_config
                  ? [
                      {
                        label: 'Duplicate',
                        icon: <CopyIcon />,
                        testId: `agent-duplicate-${item.id}`,
                        onClick: () => {
                          if (!item.agent_config) return;
                          duplicateAgent.mutate(
                            {
                              name: `Copy of ${item.name}`,
                              agentConfig: item.agent_config,
                              description: item.description ?? undefined,
                              groupName: item.group_name ?? undefined,
                            },
                            {
                              onSuccess: (config) => { void navigate(`/personas/${config.persona_id}`); },
                              onError: (err) => toast.error(err.message),
                            },
                          );
                        },
                      },
                    ]
                  : []),
                {
                  label: 'Delete',
                  icon: <TrashIcon />,
                  variant: 'destructive' as const,
                  testId: `agent-delete-${item.id}`,
                  onClick: () => {
                    setDeleteCascade(false);
                    setPendingDeleteId(item.id);
                  },
                },
              ]
        }
        items={pagedItems}
        renderItem={renderItem}
        getItemId={(p) => p.id}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search personas..."
        page={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
        totalItems={filteredItems.length}
        pageSize={pagination.pageSize}
        pageSizeOptions={pagination.pageSizeOptions}
        onPageSizeChange={pagination.onPageSizeChange}
        isLoading={workspaceLoading || systemLoading}
        headerActions={
          <ListAddButton label="New persona" testId="agent-new-button" onClick={() => { void navigate('/personas/new'); }} />
        }
        emptyState={
          <div className="text-center" data-testid="agent-library-empty">
            {workspaceError ? (
              <p className="text-body text-muted-foreground">
                Failed to load personas. Make sure the backend service is running.
              </p>
            ) : sourceScope === 'mine' && !searchValue && !groupScope ? (
              <p className="flex items-center justify-center gap-2 text-body text-muted-foreground">
                You have no personas yet —
                <button
                  type="button"
                  data-testid="agent-empty-browse-templates"
                  onClick={() => setSourceScope('templates')}
                  className="rounded-full bg-secondary px-3 py-1 text-caption font-medium text-secondary-foreground hover:bg-secondary/80"
                >
                  browse Templates
                </button>
              </p>
            ) : (
              <p className="text-body text-muted-foreground">
                {sourceScope === 'templates' && !searchValue
                  ? 'No templates found.'
                  : 'No personas match your filters.'}
              </p>
            )}
          </div>
        }
      />
    </div>
  );
}
